import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { AsyncTypeahead, Highlighter } from 'react-bootstrap-typeahead';
import { Label } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { searchConcept, searchConceptByExpression, searchCustomConcepts, searchFrequentConcepts } from '../../api/snomed';
import { HandleApiError } from "../Notifications/APIErrorHandler";
import { CustomConceptCreator } from './CustomConceptCreator';
import { useAutocompleteDropdownSize } from './hooks/useAutocompleteDropdownSize';

import 'react-bootstrap-typeahead/css/Typeahead.css';

export const SnomedPicker = (props) => {
    const { allowCustom, ...typeaheadProps } = props;
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const [createdSelection, setCreatedSelection] = useState(null);
    const dropdownSize = useAutocompleteDropdownSize();

    const handleChange = value => {
        if (createdSelection !== null)
            setCreatedSelection(value);

        if (value.length > 0) {
            if (props.multiple)
                props.onChange(props.name, value);
            else
                props.onChange(props.name, value[0]);
        }
        else
            props.onChange(props.name, null);
    };

    const handleBlur = () => {
        if (props.onBlur)
            props.onBlur(props.name, true);
    };

    const onSearch = async (term) => {
        setIsLoading(true);

        let func = null;
        let query = "";

        if (props.conceptId) {
            func = searchConcept;
            query = props.conceptId;
        }
        else {
            func = searchConceptByExpression;
            query = props.expression;
        }

        const [catalogResult, frequentResult, customResult] = await Promise.allSettled([
            func(query, encodeURIComponent(term), props.conceptType),
            searchFrequentConcepts(props.conceptType, term, dropdownSize, { conceptId: props.conceptId, expression: props.expression }),
            allowCustom ? searchCustomConcepts(props.conceptType, term, dropdownSize) : Promise.resolve({ data: [] })
        ]);

        setIsLoading(false);

        if (catalogResult.status === 'rejected')
            HandleApiError(catalogResult.reason);

        if (frequentResult.status === 'rejected')
            console.error('searchFrequentConcepts failed', frequentResult.reason);

        if (customResult.status === 'rejected')
            console.error('searchCustomConcepts failed', customResult.reason);

        const catalogOptions = catalogResult.status === 'fulfilled' && Array.isArray(catalogResult.value.data)
            ? catalogResult.value.data
            : [];
        // /concept/frequent también devuelve los términos propios del dominio (sin conceptId): sólo se muestran
        // en los campos que admiten términos propios (EA/AP y Procedimientos), no en cualquier picker de "events".
        const frequentOptions = frequentResult.status === 'fulfilled' && Array.isArray(frequentResult.value.data)
            ? frequentResult.value.data.filter(x => allowCustom || x.conceptId)
            : [];
        const customOptions = customResult.status === 'fulfilled' && Array.isArray(customResult.value.data)
            ? customResult.value.data
            : [];

        // Orden: frecuentes (ya vienen ordenados por uso desde el backend), propios del centro que todavía
        // no son frecuentes y catálogo, sin repetir y completando hasta el total del dropdown. Los propios
        // no tienen conceptId (es null en todos), así que se deduplican por id.
        const frequentConceptIds = new Set(frequentOptions.filter(x => x.conceptId).map(x => x.conceptId));
        const frequentIds = new Set(frequentOptions.map(x => x.id));
        const merged = [
            ...frequentOptions.map(x => ({ ...x, isFrequent: true, isCustom: !x.conceptId })),
            ...customOptions.filter(x => !frequentIds.has(x.id)).map(x => ({ ...x, isCustom: true })),
            ...catalogOptions.filter(x => !frequentConceptIds.has(x.conceptId))
        ].slice(0, dropdownSize);

        setOptions(merged);
    }

    const handleCustomCreated = concept => {
        const option = { ...concept, isCustom: true };
        setCreatedSelection([option]);
        handleChange([option]);
    };

    const typeahead = <AsyncTypeahead
        {...typeaheadProps}
        {...(createdSelection !== null && typeaheadProps.selected === undefined ? { selected: createdSelection } : {})}
        labelKey="description"
        filterBy={() => true}
        options={Array.isArray(options) ? options : []}
        isLoading={isLoading}
        onSearch={onSearch}
        highlightOnlyResult={true}
        delay={500}
        onChange={handleChange}
        onBlur={handleBlur}
        renderMenuItemChildren={(option, menuProps) => (
            <>
                {option.isFrequent && <>
                    <Label bsStyle="info">{t("commons.frequent")}</Label>{' '}
                </>}
                {option.isCustom && <>
                    <Label>{t("commons.customTerm.badge")}</Label>{' '}
                </>}
                <Highlighter search={menuProps.text}>{option.description}</Highlighter>
            </>
        )}
    />;

    if (!allowCustom)
        return typeahead;

    // "Agregar nuevo" siempre visible debajo del campo (RN-9), sin depender del resultado de la búsqueda.
    return <>
        {typeahead}
        <CustomConceptCreator
            conceptType={props.conceptType}
            disabled={props.disabled}
            onCreated={handleCustomCreated} />
    </>;
}

SnomedPicker.propTypes = {
    allowCustom: PropTypes.bool,
    multiple: PropTypes.bool,
    onChange: PropTypes.func,
    minLength: PropTypes.number,
    conceptId: PropTypes.string,
    expression: PropTypes.string,
    clearButton: PropTypes.bool,
    conceptType: PropTypes.string
};

SnomedPicker.defaultProps = {
    allowCustom: false,
    multiple: false,
    onChange: () => { },
    minLength: 3,
    allowNew: false,
    clearButton: true
};
